import * as React from "react";
import { Icon, Table, Tbody, Td, Th, Thead, Tr } from "../components";
import type { SubjectChoiceModel } from "./SubjectChoices";
import * as Rules from "./subjectChoiceEvaluator";
import type { SubjectChoiceSubject } from "./SubjectChoiceStore";

/**
 * The student x subject grid. Columns are grouped into bands by rule: each subject sits under the first rule, in
 * rank order, that names it explicitly (rules over every subject do not form a band), and anything no rule names
 * goes under "Other". A status column shows each row's problems, and the footer counts how many take each subject.
 */

interface Band {
    key: string;
    label: string;
    title?: string;
    subjects: SubjectChoiceSubject[];
}

/** "Mathematics (choose 1)", "Life Orientation (compulsory)", "Electives (choose at least 3)". */
function bandLabel(rule: Rules.RuleDefinition, offering: Rules.Offering) {
    if (Rules.isCompulsory(rule, offering)) return `${rule.name} (compulsory)`;
    if (Rules.isExactlyOne(rule)) return `${rule.name} (choose 1)`;
    return `${rule.name} (choose ${Rules.bounds(rule.minimum, rule.maximum)}${Rules.isCreditRule(rule) ? " credits" : ""})`;
}

function bandsOf(model: SubjectChoiceModel): Band[] {
    const bands: Band[] = [];
    const placed = new Set<string>();
    for (const rule of model.offering.rules) {
        if (rule.appliesToAllSubjects) continue;
        const subjects = model.subjects.filter((s) => rule.moduleIDs.includes(s.moduleID) && !placed.has(s.moduleID));
        if (subjects.length === 0) continue;
        subjects.forEach((s) => placed.add(s.moduleID));
        const active = model.activeRules.some((r) => r.id === rule.id);
        bands.push({
            key: rule.id,
            label: bandLabel(rule, model.offering),
            title: active ? undefined : "This rule is not checked for this grade this year",
            subjects,
        });
    }
    const other = model.subjects.filter((s) => !placed.has(s.moduleID));
    if (other.length > 0) bands.push({ key: "other", label: bands.length ? "Other" : "", subjects: other });
    return bands;
}

const checkboxClass = "h-4 w-4 rounded-xs border-[1.5px] border-border-strong text-primary";

export const SubjectChoiceGridTab: React.FC<{ model: SubjectChoiceModel }> = ({ model }) => {
    const { visible, choices, names, canWrite, isLocked, blockedReason, toggle, changed } = model;
    const bands = React.useMemo(() => bandsOf(model), [model.offering, model.activeRules, model.subjects]);
    const columns = bands.flatMap((b) => b.subjects);
    const showBands = bands.some((b) => b.label);

    const toggleColumn = (moduleID: string, on: boolean) =>
        visible.filter((id) => !isLocked(id, moduleID)).forEach((id) => toggle(id, moduleID, on));

    // Ticking a whole row leaves either/or subjects alone: picking one of each pair for the student is not a
    // choice the screen should make. Unticking clears everything that is not locked.
    const either = new Set(model.activeRules.filter(Rules.isExactlyOne).filter((r) => r.moduleIDs.length > 1).flatMap((r) => r.moduleIDs));
    const toggleRow = (studentID: string, on: boolean) =>
        columns
            .filter((s) => !isLocked(studentID, s.moduleID) && !(on && either.has(s.moduleID)))
            .forEach((s) => toggle(studentID, s.moduleID, on));
    const rowComplete = (mine: string[]) =>
        columns.length > 0 && columns.filter((s) => !either.has(s.moduleID)).every((s) => mine.includes(s.moduleID));

    const takers = (moduleID: string) => model.order.filter((id) => (choices[id] ?? []).includes(moduleID)).length;

    return (
        <Table>
            <Thead>
                {showBands && (
                    <tr>
                        <Th />
                        <Th />
                        {bands.map((band) => (
                            <th
                                key={band.key}
                                colSpan={band.subjects.length}
                                title={band.title}
                                className={`border-b border-l border-border px-3 py-2 text-center text-xs font-semibold uppercase tracking-wide text-text-weak ${
                                    band.title ? "italic opacity-70" : ""
                                }`}
                            >
                                {band.label}
                            </th>
                        ))}
                    </tr>
                )}
                <tr>
                    <Th>Student</Th>
                    <Th align="center">Status</Th>
                    {columns.map((s) => {
                        const eligible = visible.filter((id) => !isLocked(id, s.moduleID));
                        const all = eligible.length > 0 && eligible.every((id) => (choices[id] ?? []).includes(s.moduleID));
                        return (
                            <Th key={s.moduleID} align="center" title={`${s.termCount} term(s) in this grade${s.credits != null ? `, ${s.credits} credits` : ""}`}>
                                <div className="flex flex-col items-center gap-1">
                                    <span>{s.name}</span>
                                    {canWrite && (
                                        <input
                                            type="checkbox"
                                            aria-label={`Everyone takes ${s.name}`}
                                            title="Tick or untick for every student shown"
                                            className={checkboxClass}
                                            checked={all}
                                            onChange={(e) => toggleColumn(s.moduleID, e.target.checked)}
                                        />
                                    )}
                                </div>
                            </Th>
                        );
                    })}
                </tr>
            </Thead>
            <Tbody>
                {visible.map((studentID) => {
                    const mine = choices[studentID] ?? [];
                    const problems = model.findings(studentID);
                    const tint = problems.length ? "bg-danger-soft" : changed.includes(studentID) ? "bg-primary-soft" : "";
                    return (
                        <Tr key={studentID} className={tint}>
                            <Td>
                                <div className="flex items-center gap-2">
                                    {canWrite && (
                                        <input
                                            type="checkbox"
                                            aria-label={`All subjects for ${names(studentID)}`}
                                            title="Tick or untick every subject for this student (either/or choices are left to you)"
                                            className={checkboxClass}
                                            checked={rowComplete(mine)}
                                            onChange={(e) => toggleRow(studentID, e.target.checked)}
                                        />
                                    )}
                                    <div className="min-w-0">
                                        <div className="truncate font-medium text-text-strong">{names(studentID)}</div>
                                        {names(studentID) !== studentID && <div className="truncate text-xs text-text-weak">{studentID}</div>}
                                    </div>
                                </div>
                            </Td>
                            <Td align="center">
                                {problems.length === 0 ? (
                                    <span className="text-success-text" title="Meets every rule">
                                        <Icon icon={["fad", "circle-check"]} />
                                    </span>
                                ) : (
                                    <span
                                        className="inline-flex items-center gap-1 text-danger-text"
                                        title={problems.map((f) => f.message).join("\n")}
                                        aria-label={`${problems.length} problem${problems.length === 1 ? "" : "s"}: ${problems.map((f) => f.message).join(" ")}`}
                                    >
                                        <Icon icon={["fad", "triangle-exclamation"]} />
                                        {problems.length}
                                    </span>
                                )}
                            </Td>
                            {columns.map((s) => {
                                const locked = isLocked(studentID, s.moduleID);
                                const blocked = blockedReason(studentID, s.moduleID);
                                return (
                                    <Td key={s.moduleID} align="center">
                                        <input
                                            type="checkbox"
                                            aria-label={`${names(studentID)} takes ${s.name}`}
                                            title={locked ? "A term of this subject is Completed and cannot be changed here" : blocked}
                                            className={checkboxClass}
                                            checked={mine.includes(s.moduleID)}
                                            disabled={!canWrite || locked || !!blocked}
                                            onChange={(e) => toggle(studentID, s.moduleID, e.target.checked)}
                                        />
                                    </Td>
                                );
                            })}
                        </Tr>
                    );
                })}
                <tr className="border-t-2 border-border bg-surface-muted text-sm">
                    <Td>
                        <span className="font-medium text-text">Students taking it</span>
                    </Td>
                    <Td />
                    {columns.map((s) => (
                        <Td key={s.moduleID} align="center">
                            <span className="font-medium text-text">{takers(s.moduleID)}</span>
                        </Td>
                    ))}
                </tr>
            </Tbody>
        </Table>
    );
};
